// SF4 — Promotion ROI, with SF3 depth bands and SF6 spikes beside it.
//
// REDEMPTIONS ARE NOT RETURN. That is the whole feature. A promotion with a
// thousand redemptions can lose twenty thousand rupees, because most of those
// buyers would have bought anyway and each one cost the margin given up between
// the two prices — not the discount on the label, which is a bigger and more
// flattering number. Seller Central shows the redemptions; the four cost lines
// below are what it does not.
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FiAlertTriangle, FiChevronDown, FiChevronRight, FiInfo } from 'react-icons/fi';

import AuthContext from '../../../../context/AuthContext';
import local from '../Promotions.module.css';
import { getReturns } from '../services/promotionsApi';

const inr = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });

const Net = ({ value }) => {
  if (value === null || value === undefined) return <span className={local.blank}>—</span>;
  return (
    <span className={value > 0 ? local.paid : local.lost}>
      {value >= 0 ? '+' : '−'}₹{inr.format(Math.abs(Math.round(value)))}
    </span>
  );
};

const PromotionReturns = () => {
  const authCtx = useContext(AuthContext);
  const token = authCtx?.token;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true); setError(null);
    try { setData(await getReturns(token)); }
    catch (err) { setError(err); setData(null); }
    finally { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const rows = data?.promotions || [];

  return (
    <section className={local.card}>
      <div className={local.cardHead}>
        <h2 className={local.cardTitle}>Did each promotion actually make money</h2>
        {data && (
          <span className={local.muted}>{data.paid_back} paid back</span>
        )}
      </div>
      <p className={local.muted}>
        Redemptions are not return. Most redeemed units would have sold anyway,
        and what they cost is the margin given up between the two prices — not
        the discount on the label.
      </p>

      {error && (
        <div className={`${local.banner} ${error.reportUnavailable ? local.bannerWarn : local.bannerError}`}>
          <FiAlertTriangle className={local.bannerIcon} />
          <div>
            <strong>
              {error.notConnected ? 'No Amazon account connected'
                : error.reportUnavailable ? 'Data not ready' : 'Could not load promotion returns'}
            </strong>
            <div>{error.message}</div>
          </div>
        </div>
      )}

      {!error && (
        <div className={local.tableScroll}>
          <table className={local.table}>
            <thead>
              <tr>
                <th />
                <th>Promotion</th>
                <th>Ran</th>
                <th className={local.numeric}>Incremental units</th>
                <th className={local.numeric}>Net</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td className={local.emptyCell} colSpan={5}>
                  {loading ? 'Loading…' : 'No promotions found in the synced reports.'}
                </td></tr>
              ) : rows.map((row) => (
                <React.Fragment key={row.id || row.name}>
                  <tr className={local.drillable}
                    onClick={() => setOpen(open === (row.id || row.name) ? null : (row.id || row.name))}>
                    <td className={local.rankCell}>
                      {open === (row.id || row.name) ? <FiChevronDown /> : <FiChevronRight />}
                    </td>
                    <td>{row.name || row.id} <span className={local.context}>({row.type})</span></td>
                    <td className={local.context}>{row.start} → {row.end}</td>
                    <td className={`${local.numeric} ${local.context}`}>
                      {row.incremental_units ?? '—'}
                    </td>
                    <td className={local.numeric}>
                      {row.roi ? <Net value={row.roi.net} />
                        : <span className={local.blank} title={row.reason}>—</span>}
                    </td>
                  </tr>

                  {open === (row.id || row.name) && (
                    <tr className={local.nested}>
                      <td />
                      <td colSpan={4}>
                        {!row.roi ? (row.reason || 'Not measurable.') : (
                          <>
                            {/* The four cost lines, each named. A single ROI
                                figure hides which one sank it. */}
                            <div className={local.costLine}>
                              <span className={local.costLabel}>
                                Buyers who would have bought anyway
                                ({row.roi.would_have_bought_anyway} units)
                              </span>
                              <span className={local.lost}>
                                −₹{inr.format(Math.round(row.roi.costs.cannibalisation.amount))}
                              </span>
                            </div>
                            <div className={local.costLine}>
                              <span className={local.costLabel}>
                                Amazon's promotion fee ({row.roi.fee_basis})
                              </span>
                              <span className={local.lost}>
                                −₹{inr.format(Math.round(row.roi.costs.promotion_fee.amount))}
                              </span>
                            </div>
                            <div className={local.costLine}>
                              <span className={local.costLabel}>Returns on promotion orders</span>
                              <span className={local.lost}>
                                −₹{inr.format(Math.round(row.roi.costs.returns.amount))}
                              </span>
                            </div>
                            <div className={local.costLine}>
                              <span className={local.costLabel}>
                                Incremental margin ({row.roi.incremental_margin.units} units)
                              </span>
                              <span className={local.paid}>
                                +₹{inr.format(Math.round(row.roi.incremental_margin.amount))}
                              </span>
                            </div>
                            <div className={local.costLine}>
                              <strong className={local.costLabel}>
                                Break-even needed
                              </strong>
                              <strong>
                                {row.roi.break_even_units ?? '—'} incremental units
                              </strong>
                            </div>
                            <div className={local.muted} style={{ marginTop: 6 }}>
                              Margin per unit: ₹{row.roi.margin_per_unit.at_list} at list,
                              ₹{row.roi.margin_per_unit.at_promo} on promotion.
                              {row.carried_by?.length > 0 && ` Carried by ${row.carried_by.join(', ')}.`}
                              {row.no_lift?.length > 0 && ` No measurable lift on ${row.no_lift.join(', ')}.`}
                            </div>
                          </>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data?.notes?.length > 0 && (
        <ul className={local.noteList}>
          {data.notes.slice(0, 6).map((note) => (
            <li key={note}><FiInfo className={local.noteIcon} />{note}</li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default PromotionReturns;
